import type {
  ContractRecordMap,
  PlannableOverlapExtended
} from '@libs/data-access/entities'
import { Alert, Badge, Flex, MultiSelect, Select } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { formatErrors } from '@spa/components/FormFields/helpers'
import type { RestIterable } from '@spa/constants/RestTypes'
import moment from 'moment'
import type { FC } from 'react'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'

import { ContractHasRestOnPeriod } from '../Alerts'
import { RestTypeField } from '../RestTypeField/RestTypeField'

import { useFormattedContractOptions } from './hooks/useFormattedContractOptions'

type Props = {
  restTypeOptions: RestIterable
  restIsLocked?: boolean
  isCustom: boolean
  disabledRestTypeField?: boolean
  disabledContractField?: boolean
  plannableOverlapExtended: PlannableOverlapExtended
  startsAt: string
  setValue: Function
  isMulti: boolean
  selectedContracts: {
    label: string
    value: number
  }[]
  contracts: ContractRecordMap
  disableCustomRest?: boolean
  control: Control
  errors: FieldErrors
  userContractsSize?: number
  restType: string
  restCantBeOnGuaranteePh?: boolean
}
const RestTypeAndContract: FC<Props> = ({
  restTypeOptions,
  restIsLocked,
  isCustom,
  disabledContractField,
  disabledRestTypeField,
  isMulti,
  startsAt,
  contracts,
  disableCustomRest,
  control,
  errors,
  setValue,
  selectedContracts,
  userContractsSize,
  restType,
  restCantBeOnGuaranteePh,
  plannableOverlapExtended
}) => {
  const { t } = usePlanningFormTranslation()
  const currentDay = moment(startsAt).format('dddd D MMMM YYYY')
  const plannableIfWeeklyRest =
    (plannableOverlapExtended.total_weekly_rest_count ?? 0) +
    (plannableOverlapExtended.total_hourly_rest_count ?? 0)
  const plannableIfOtherRest =
    (plannableOverlapExtended.total_other_rest_count ?? 0) +
    (plannableOverlapExtended.total_hourly_rest_count ?? 0)
  const plannableIfHourlyRest =
    restTypeOptions.get(restType)?.type === 'hour'
      ? plannableOverlapExtended.total_weekly_rest_count ?? 0
      : 0
  const hasPlannableOverlap =
    restType === 'weekly_rest'
      ? plannableIfWeeklyRest > 0
      : plannableIfOtherRest + plannableIfHourlyRest > 0

  const formattedOptions = useFormattedContractOptions(
    contracts,
    plannableOverlapExtended,
    restType
  )
  const handleChange = (newValues: string[]) => {
    const updatedValues = newValues.map(item => {
      return formattedOptions.find(option => option.value === item)
    })

    setValue('user_contract_ids', updatedValues)
  }

  const disabled =
    restIsLocked || disabledContractField || isCustom || disableCustomRest

  return (
    <div className='grid gap-3'>
      <RestTypeField
        restTypeOptions={restTypeOptions}
        restType={restType}
        // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
        restIsLocked={restIsLocked}
        isCustom={isCustom}
        disabled={disabledRestTypeField}
        setValue={setValue}
        selectedContracts={selectedContracts}
        rightComponent={currentDay}
        control={control}
        errors={errors}
      />
      {restCantBeOnGuaranteePh && (
        <Alert intent='semantic.blue'>
          {t('restForm.restCantBeOnGuaranteePh')}
        </Alert>
      )}
      <Controller
        name='user_contract_ids'
        control={control}
        render={({ field: { value, onChange } }) => {
          if (isMulti) {
            return (
              <MultiSelect
                clearable
                searchable
                searchAdapter='fuzzyIncludingGroup'
                fillWidth
                dropdownHeight='m'
                dropdownWidth='target'
                options={formattedOptions}
                onChange={handleChange}
                placeholder={t('restForm.restTypeAndContractSelectPlaceholder')}
                value={value.map(x => x.value)}
                errorMessage={formatErrors({
                  inputName: 'user_contract_ids',
                  errors
                })}
                disabled={disabled}
                label={
                  <Flex row className='flex-nowrap items-center gap-2'>
                    {t('restForm.fieldContractLabel', {
                      count: userContractsSize
                    })}
                    {Boolean(userContractsSize) && (
                      <Badge size='s' label={`${userContractsSize}`} />
                    )}
                  </Flex>
                }
              />
            )
          }

          return (
            <Select
              searchable
              searchAdapter='fuzzyIncludingGroup'
              fillWidth
              dropdownHeight='m'
              dropdownWidth='target'
              options={formattedOptions}
              onChange={val => onChange([{ value: val }])}
              placeholder={t('component.fieldUserContract.placeholder')}
              value={value[0]?.value}
              disabled={disabled}
              label={
                <Flex row className='flex-nowrap items-center '>
                  {t('restForm.fieldContractLabel', {
                    count: userContractsSize
                  })}
                </Flex>
              }
            />
          )
        }}
      />

      {hasPlannableOverlap && (
        <ContractHasRestOnPeriod
          // @ts-ignore TO DO: fix (strictNullChecks errors) (https://snapshiftapp.atlassian.net/browse/COP-333)
          isMulti={plannableOverlapExtended?.contracts_rest_overlap.length > 1}
        />
      )}
    </div>
  )
}

export default RestTypeAndContract
